import {useCallback, useEffect, useRef, useState} from 'react'
import {useChat, useLocalParticipant} from '@livekit/components-react'
import {playMessageSound} from './chatSound'

export interface ChatPanelState {
  isOpen: boolean
  unreadCount: number
  toggleChat: () => void
  closeChat: () => void
}

export function useChatPanel() {
  const {chatMessages, send, isSending} = useChat()
  const {localParticipant} = useLocalParticipant()
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const prevLengthRef = useRef(chatMessages.length)
  const isOpenRef = useRef(isOpen)
  const localIdentity = localParticipant?.identity ?? null

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  useEffect(() => {
    const newCount = chatMessages.length - prevLengthRef.current
    if (newCount <= 0) {
      prevLengthRef.current = chatMessages.length
      return
    }
    const hasNewMessageFromOther = chatMessages
      .slice(-newCount)
      .some(msg => msg.from?.identity !== localIdentity)
    if (hasNewMessageFromOther) {
      playMessageSound()
    }
    if (!isOpenRef.current) {
      setUnreadCount(prev => prev + newCount)
    }
    prevLengthRef.current = chatMessages.length
  }, [chatMessages, localIdentity])

  const openChat = useCallback(() => {
    setIsOpen(true)
    setUnreadCount(0)
  }, [])

  const closeChat = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggleChat = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) setUnreadCount(0)
      return !prev
    })
  }, [])

  return {
    isOpen,
    unreadCount,
    chatMessages,
    send,
    isSending,
    openChat,
    closeChat,
    toggleChat,
  }
}
